import {
  CheckInput,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
} from '@octano/global-ui';
import { Fragment, ReactChild, useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { AzureFilesUploaderControlled } from '../../../../../components/AzureFilesUploader/AzureFileUploaderControlled';
import { SectionTitle } from '../../../../../components/text';
import { useValidations } from '../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../locales/translations';
import { formatToOnlyPositiveIntegers } from '../../../../../utils/currency';
import { useProgramFormLoaderData } from '../../hooks/ProgramFormLoader';
import { ProgramCurriculum } from './ProgramCurriculum';
import { ProgramUnit } from '../../api';
import {
  FileRequiredTypes,
  SecureResponseFile,
} from '../../../../../components/AzureFilesUploader/types';

export interface ProgramFormValues {
  academicLevel: SelectOptionType | null;
  name: string;
  code: string;
  modality: SelectOptionType | null;
  school: SelectOptionType | null;
  totalHours: number;
  activityHours: { [activityTypeId: string]: number };
  description: string;
  hasCertificate: boolean;
  units: ProgramUnit[];
  curriculumImage?: SecureResponseFile | null;
}

const getDefaultValues = (): ProgramFormValues => ({
  academicLevel: null,
  name: '',
  code: '',
  modality: null,
  school: null,
  totalHours: 0,
  activityHours: {},
  description: '',
  hasCertificate: false,
  units: [{ name: 'Unidad 1', subjects: [] }],
  curriculumImage: null,
});

export interface ProgramFormProps {
  children: (props: { isSubmitting: boolean }) => ReactChild;
  onSubmit: (
    values: ProgramFormValues,
  ) => Promise<'CONNECTION' | 'HTTP_ERROR' | undefined>;
  initialValues?: Partial<ProgramFormValues>;
  isEditMode?: boolean;
  isInActiveOffer?: boolean;
}

export function ProgramForm(props: ProgramFormProps) {
  const {
    onSubmit: onSubmitProp,
    children,
    initialValues,
    isEditMode,
    isInActiveOffer,
  } = props;
  const {
    schools,
    modalities,
    modalitiesDeliveryMode,
    activityTypes,
    subjects,
    academicLevels,
  } = useProgramFormLoaderData();

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'programForm';

  const methods = useForm<ProgramFormValues>({
    mode: 'onSubmit',
    defaultValues: { ...getDefaultValues(), ...initialValues },
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    reset,
  } = methods;

  const { msgValidations, rulesPatternDecimal } = useValidations();

  // Reset form when initialValues change (for edit mode)
  useEffect(() => {
    if (initialValues && isEditMode) {
      reset({ ...getDefaultValues(), ...initialValues });
    }
  }, [initialValues, isEditMode, reset]);

  const [
    activityHoursValues,
    selectedModality,
    units,
    selectedAcademicLevel,
  ] = watch(['activityHours', 'modality', 'units', 'academicLevel']);

  // Calculate total hours based on activity hours
  const totalHours = useMemo(() => {
    if (!activityHoursValues) {
      return 0;
    }
    return Object.values(activityHoursValues).reduce(
      (sum, hours) => sum + Number(hours || 0),
      0,
    );
  }, [activityHoursValues]);

  useEffect(() => {
    setValue('totalHours', totalHours);
  }, [totalHours, setValue]);

  // Clear selected subjects if academic level changes and subjects do not match new level
  useEffect(() => {
    if (units && selectedAcademicLevel) {
      let unitsChanged = false;
      const newUnits = units.map((unit) => {
        const validSubjects = unit.subjects.filter((subject) => {
          const found = subjects.find((s) => s.id === subject.id);
          return (
            found && found.academicLevel?.id === selectedAcademicLevel.value
          );
        });
        if (validSubjects.length !== unit.subjects.length) {
          unitsChanged = true;
          return { ...unit, subjects: validSubjects };
        }
        return unit;
      });
      if (unitsChanged) {
        setValue('units', newUnits);
      }
    }
  }, [selectedAcademicLevel, units, subjects, setValue]);

  const filteredSubjects = useMemo(() => {
    if (!selectedModality || !selectedAcademicLevel) {
      return [];
    }
    const programDeliveryMode =
      modalitiesDeliveryMode[selectedModality.value as number] || '';

    return subjects.filter((subject) => {
      // Academic Level filter
      if (subject.academicLevel?.id !== selectedAcademicLevel.value) {
        return false;
      }
      const subjectDeliveryMode = subject.modality?.deliveryMode || '';

      switch (programDeliveryMode) {
        case 'OL':
          return subjectDeliveryMode === 'OL' || subjectDeliveryMode === 'OA';
        case 'OA':
          return subjectDeliveryMode === 'OA';
        case 'PR':
          return subjectDeliveryMode === 'PR';
        case 'SP':
          return true;
        default:
          return true;
      }
    });
  }, [
    selectedModality,
    selectedAcademicLevel,
    subjects,
    modalitiesDeliveryMode,
  ]);

  // Validate units before submit
  const validateUnits = useCallback(() => {
    let isValid = true;
    let totalSubjects = 0;

    if (!units || units.length === 0) {
      setError('units', {
        type: 'manual',
        message: t(`${prefix}.validation.atLeastOneUnit`),
      });
      return false;
    }

    units.forEach((unit, index) => {
      if (!unit.name || unit.name.trim() === '') {
        setError(`units.${index}.name` as any, {
          type: 'manual',
          message: msgValidations.required,
        });
        isValid = false;
      }

      if (!unit.subjects || unit.subjects.length === 0) {
        setError(`units.${index}.subjects` as any, {
          type: 'manual',
          message: t(`${prefix}.validation.atLeastOneSubjectPerUnit`),
        });
        isValid = false;
      } else {
        totalSubjects += unit.subjects.length;
      }
    });

    if (totalSubjects < 2) {
      setError('units', {
        type: 'manual',
        message: t(`${prefix}.validation.atLeastTwoSubjects`),
      });
      isValid = false;
    }

    return isValid;
  }, [units, setError, t, msgValidations.required]);

  const onSubmit = useCallback(
    async (formValues: ProgramFormValues) => {
      if (!validateUnits()) {
        return;
      }
      await onSubmitProp(formValues);
    },
    [onSubmitProp, validateUnits],
  );

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      clearErrors();
      handleSubmit(onSubmit)(e);
    },
    [clearErrors, handleSubmit, onSubmit],
  );

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form onSubmit={handleFormSubmit}>
          <Row>
            <Col xs={12}>
              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.title`)} />
                  <p>{t(`${prefix}.subtitle`)}</p>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <Select
                        name="academicLevel"
                        label={t(`${prefix}.academicLevel`, 'Nivel Académico')}
                        options={academicLevels}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="name"
                        label={t(`${prefix}.name`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 255,
                            message: t('common.validations.maxLength', {
                              length: 255,
                            }),
                          },
                        }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="code"
                        label={t(`${prefix}.code`)}
                        control={control}
                        rules={{
                          required: msgValidations.required,
                          maxLength: {
                            value: 50,
                            message: t('common.validations.maxLength', {
                              length: 50,
                            }),
                          },
                        }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <Select
                        name="modality"
                        label={t(`${prefix}.modality`)}
                        options={modalities}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                    <Col xs={12} md={6} className="pt-2">
                      <Select
                        name="school"
                        label={t(`${prefix}.school`)}
                        options={schools}
                        control={control}
                        rules={{ required: msgValidations.required }}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} md={6} className="pt-2">
                      <TextInput
                        name="totalHours"
                        label={t(`${prefix}.totalHours`)}
                        control={control}
                        disabled
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              {activityTypes.length > 0 && (
                <Row className="mb-4">
                  <Col xs={12} className="pb-2">
                    <SectionTitle text={t(`${prefix}.activityHoursTitle`)} />
                  </Col>
                  {activityTypes.map((activityType) => (
                    <Col xs={12} md={6} key={activityType.id} className="pt-2">
                      <TextInput
                        name={`activityHours.${activityType.id}` as any}
                        control={control}
                        label={activityType.name}
                        rules={{
                          required: msgValidations.required,
                          pattern: rulesPatternDecimal,
                        }}
                        formatter={formatToOnlyPositiveIntegers}
                        disabled={isInActiveOffer}
                      />
                    </Col>
                  ))}
                </Row>
              )}

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.description`)} />
                </Col>
                <Col xs={12} className="pt-2">
                  <TextAreaInput
                    name="description"
                    label={''}
                    control={control}
                    maxLength={200}
                    height={100}
                    showCharCounter
                    disabled={isInActiveOffer}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.curriculumTitle`)} />
                </Col>
                <Col xs={12} className="pt-3">
                  <ProgramCurriculum
                    control={control}
                    subjects={filteredSubjects}
                    selectedModality={selectedModality}
                    isInActiveOffer={isInActiveOffer}
                  />
                  {Object.keys(errors).length > 0 && (
                    <p className="text-danger small mt-2">
                      {Array.isArray(errors.units) &&
                        errors.units.map((error: any) => {
                          return error?.subjects?.message ?? '';
                        })}
                      {(errors.units as any)?.message}
                    </p>
                  )}
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12} md={6}>
                  <SectionTitle text={t(`${prefix}.curriculumImage.title`)} />
                  <span className="fs-16">
                    {t(`${prefix}.curriculumImage.description`)}
                  </span>
                </Col>
                <Col xs={12} md={6}>
                  <AzureFilesUploaderControlled
                    name="curriculumImage"
                    control={control}
                    accept={['images']}
                    label={t(`${prefix}.curriculumImage.inputTitle`)}
                    btnText={t(`${prefix}.curriculumImage.inputBtn`)}
                    fileType={FileRequiredTypes.AcademicCalendar}
                    disabled={isInActiveOffer}
                  />
                </Col>
              </Row>

              <Row className="mb-4">
                <Col xs={12}>
                  <SectionTitle text={t(`${prefix}.certificate`)} />
                </Col>
                <Col xs={12} md={6} className="pt-2">
                  <CheckInput
                    name="hasCertificate"
                    label={t(`${prefix}.hasCertificate`)}
                    control={control}
                    disabled={isInActiveOffer}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          {children({ isSubmitting })}
        </Form>
      </FormProvider>
    </Fragment>
  );
}
